
import {
  Box,
  Typography
} from '@mui/material';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React from 'react';
import * as ds from './dataService';
import { theme } from 'theme';
import {
  useNavigate,
  useParams
} from 'react-router-dom';


export type ITimelineDuration = 'MONTH' | 'QUARTER' | 'YEAR';


// Define properties for the GanttChart component
interface GanttChartProps {
  timelineStartDate: DateTime;
  timelineTimeUnit: ITimelineDuration;
}

export const FC = (props: GanttChartProps) => {
  const params = useParams();
  const { data } = ds.useQuery(params.projectId ?? '');
  const navigate = useNavigate();

  // Get start and end of the displayed month
  const { startPeriod: timelineStartPeriod, endOfPeriod: timelineEndPeriod } = __getStartAndEndOfTimePeriod(props);
  const milestones = data.milestones ?? [];

  // Filter milestones that are active in the displayed month
  const activeMilestones = milestones.filter((milestone) => {
    const startDate = milestone.startDate;
    const endDate = milestone.startDate.plus({
      days: milestone.durationInDays
    });
    return (
      (startDate >= timelineStartPeriod && startDate <= timelineEndPeriod)
      || (endDate >= timelineStartPeriod && endDate <= timelineEndPeriod)
      || (startDate <= timelineStartPeriod && endDate >= timelineEndPeriod)
    );
  });

  const { durationFromPeriodStartToEnd, tickWidth } = __calculateTickWidth(timelineEndPeriod, timelineStartPeriod, props.timelineTimeUnit);



  return (
    <Box
      sx={{
        overflowX: 'clip'
      }}
    >

      {/* GANNT TICKS HEADER */}
      <Box
        position='relative'
        display='flex'
        marginLeft={33}
      >

        {_.times(durationFromPeriodStartToEnd ?? 0, (i) => (
          <Box
            key={i}
            left={`${tickWidth * i}%`}
            width={props.timelineStartDate.daysInMonth ? `${tickWidth}%` : '2.54%'}
            content='""'
          >
            <Typography variant='body1'>{__calculateLabel(i, props.timelineTimeUnit, timelineStartPeriod)}</Typography>
          </Box>
        ))}
      </Box>
      {/* MILESTONES */}
      {
        activeMilestones.map((milestone, index) => {
          const startDate = milestone.startDate;
          const endDate = milestone.endDate;

          // Calculate start offset (as percentage) relative to the start of the month
          const { startOffset, durationPercent } = __calculatePosition(startDate, timelineStartPeriod, timelineEndPeriod, endDate, props.timelineTimeUnit);



          return (
            <Box
              key={index}
              bgcolor={`${theme.palette.customGray.light}`}
              onClick={() => {
                navigate(milestone.id);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box
                display='flex'
                flexDirection='column'
                overflow='clip'
                width={300}
              >
                <Typography
                  variant='body2'
                  sx={{
                  }}
                >
                  {milestone.title}
                </Typography>
                <Typography
                  variant='bodyBold'
                  sx={{
                    // width: 300
                  }}
                >
                  {`From ${milestone.startDate.toFormat('LLL,dd yy')}' to ${milestone.endDate.toFormat('LLL, dd yy')}'`}
                </Typography>
                <Typography
                  variant='bodyBold'
                  sx={{
                    // width: 300
                  }}
                >
                  {`${milestone.endDate.diff(milestone.startDate).as('days')
                    .toFixed(0)} Days`}
                </Typography>
              </Box>
              <Box
                width='100%'
                paddingY='.5rem'
              >

                {/* MILESTONE */}
                <Box
                  onClick={() => navigate(milestone.id)}
                  sx={{
                    position: 'relative',
                    height: '3.875rem',
                    width: '100%',

                    backgroundColor: `${theme.palette.customGray.light}`,
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      left: `${startOffset}%`,
                      width: `${durationPercent}%`,
                      minWidth: '1rem',
                      height: '100%',
                      borderRadius: '.75rem',
                      backgroundColor: `${theme.palette.info.main}`,
                      display: 'flex',
                      alignContent: 'center'
                    }
                  }}
                >
                  <Box
                    display='flex'
                    left={`${startOffset + 0.5}%`}
                    top='.75rem'
                    flexDirection='column'
                    height='2.6rem'
                    position='relative'
                  >
                    <Typography
                      color={theme.palette.primary.contrastText}
                      variant='bodyBold'
                    >

                      {/* {milestone.title} */}
                    </Typography>
                    <Typography
                      variant='bodyBold'
                      color={theme.palette.primary.contrastText}
                    >

                      {/* {`From ${milestone.startDate.toFormat('LLL,dd yy')}' to ${milestone.endDate.toFormat('LLL, dd yy')}'`} */}
                    </Typography>
                  </Box>


                  <Box
                    display='flex'
                    width='100%'
                  >
                    {_.times(__getNumberOfLines(props.timelineTimeUnit, timelineStartPeriod, timelineEndPeriod), (i) => (
                      <Box
                        key={i}
                        // position='relative'
                        height='4.5rem'
                        marginTop='-3rem'
                        borderLeft={`1px dashed ${theme.palette.secondary.darker}`}
                        width={`${tickWidth}%`}
                        content='""'
                      >

                      </Box>
                    ))}
                  </Box>
                </Box>


              </Box>

            </Box>
          );
        })
      }

    </Box>
  );
};

const __quarterStart2Month: Record<number, number> = {
  1: 1,
  2: 4,
  3: 7,
  4: 10
};


function __getNumberOfLines(duration: ITimelineDuration, periodStartDate: DateTime, periodEndDate: DateTime): number {
  switch (duration) {
    case 'YEAR':
      return 12;
    case 'QUARTER':
      const durationFromPeriodStartToEnd = DateTime
        .fromMillis(periodEndDate.toMillis())
        .diff(DateTime.fromMillis(Math.max(periodStartDate.toMillis())), 'weeks').weeks + 1;
      return durationFromPeriodStartToEnd;
    default:
      return periodStartDate.daysInMonth ?? 31;
  }

}

function __calculatePosition(startDate: DateTime<boolean>, timelineStartPeriod: DateTime<boolean>, timelineEndPeriod: DateTime<boolean>, endDate: DateTime<boolean>, unit: ITimelineDuration) {
  let startOffset = 0;
  let duration = 0;
  let durationPercent = 0;

  switch (unit) {
    case 'MONTH':
      startOffset = Math.max(0, startDate.diff(timelineStartPeriod, 'days').days / timelineEndPeriod.diff(timelineStartPeriod, 'days').days * 100);
      duration = DateTime.fromMillis(Math.min(timelineEndPeriod.toMillis(), endDate.toMillis()))
        .diff(DateTime.fromMillis(Math.max(timelineStartPeriod.toMillis(), startDate.toMillis())), 'days')
        .days;
      // Calculate duration (as percentage of the month)
      durationPercent = duration >= 1 ? duration / timelineEndPeriod.diff(timelineStartPeriod, 'days').days * 100 : 1 / timelineEndPeriod.diff(timelineStartPeriod, 'days').days * 100;
      break;
    case 'QUARTER':
      startOffset = Math.max(0, startDate.diff(timelineStartPeriod, 'weeks').weeks / timelineEndPeriod.diff(timelineStartPeriod, 'weeks').weeks * 100);

      duration = DateTime.fromMillis(Math.min(timelineEndPeriod.toMillis(), endDate.toMillis()))
        .diff(DateTime.fromMillis(Math.max(timelineStartPeriod.toMillis(), startDate.toMillis())), 'weeks')
        .weeks;
      // Calculate duration (as percentage of the month)
      durationPercent = duration >= 1 ? duration / timelineEndPeriod.diff(timelineStartPeriod, 'weeks').weeks * 100 : 1 / timelineEndPeriod.diff(timelineStartPeriod, 'weeks').weeks * 100;
      break;
    case 'YEAR':
      startOffset = Math.max(0, startDate.diff(timelineStartPeriod, 'months').months / timelineEndPeriod.diff(timelineStartPeriod, 'months').months * 100);

      duration = DateTime.fromMillis(Math.min(timelineEndPeriod.toMillis(), endDate.toMillis()))
        .diff(DateTime.fromMillis(Math.max(timelineStartPeriod.toMillis(), startDate.toMillis())), 'months')
        .months;
      // Calculate duration (as percentage of the month)
      durationPercent = duration >= 0 ? duration / timelineEndPeriod.diff(timelineStartPeriod, 'months').months * 100 : 1 / timelineEndPeriod.diff(timelineStartPeriod, 'months').months * 100;
      break;

    default:
      break;
  }

  return {
    startOffset, durationPercent
  };
}

function __calculateLabel(i: number, timelineTimeUnit: ITimelineDuration, periodStartDate: DateTime): React.ReactNode {
  switch (timelineTimeUnit) {
    case 'MONTH':
      return i + 1;
    case 'QUARTER':
      return periodStartDate.plus({
        weeks: i
      }).toFormat('D');
    case 'YEAR':
      return periodStartDate.plus({
        months: i
      }).toFormat('LLL');
  }

}

function __calculateTickWidth(timelineEndPeriod: DateTime<boolean>, timelineStartPeriod: DateTime<boolean>, duration: ITimelineDuration) {

  if (duration === 'MONTH') {
    const durationFromPeriodStartToEnd = DateTime
      .fromMillis(timelineEndPeriod.toMillis())
      .diff(DateTime.fromMillis(Math.max(timelineStartPeriod.toMillis())), 'days').days;

    // Calculate duration (as percentage of the month)
    const tickWidth = durationFromPeriodStartToEnd >= 1 ? durationFromPeriodStartToEnd / timelineEndPeriod.diff(timelineStartPeriod, 'days').days * 100 : 1 / timelineEndPeriod.diff(timelineStartPeriod, 'days').days * 100;
    return {
      durationFromPeriodStartToEnd, tickWidth
    };
  }
  if (duration === 'QUARTER') {
    const durationFromPeriodStartToEnd = DateTime
      .fromMillis(timelineEndPeriod.toMillis())
      .diff(DateTime.fromMillis(Math.max(timelineStartPeriod.toMillis())), 'weeks').weeks + 1;

    // Calculate duration (as percentage of the month)
    const tickWidth = durationFromPeriodStartToEnd >= 1 ? durationFromPeriodStartToEnd / timelineEndPeriod.diff(timelineStartPeriod, 'weeks').weeks * 100 : 1 / timelineEndPeriod.diff(timelineStartPeriod, 'weeks').weeks * 100;
    return {
      durationFromPeriodStartToEnd, tickWidth
    };
  } else {
    const durationFromPeriodStartToEnd = DateTime
      .fromMillis(timelineEndPeriod.toMillis())
      .diff(DateTime.fromMillis(Math.max(timelineStartPeriod.toMillis())), 'months').months + 1;

    // Calculate duration (as percentage of the month)
    const tickWidth = durationFromPeriodStartToEnd >= 1 ? durationFromPeriodStartToEnd / timelineEndPeriod.diff(timelineStartPeriod, 'months').months * 100 : 1 / timelineEndPeriod.diff(timelineStartPeriod, 'months').months * 100;
    return {
      durationFromPeriodStartToEnd, tickWidth
    };
  }
}

function __getStartAndEndOfTimePeriod(props: GanttChartProps) {
  const timelineYear = props.timelineStartDate.year;
  const timelineMonth = props.timelineStartDate.month;
  const timelineQuarter = props.timelineStartDate.quarter;
  let startPeriod: DateTime = DateTime.fromObject({
    day: 1, month: timelineMonth, year: timelineYear
  });
  let endOfPeriod: DateTime = startPeriod.endOf('month');

  switch (props.timelineTimeUnit) {
    case 'MONTH':

      endOfPeriod = startPeriod.endOf('month');
      break;
    case 'QUARTER':
      startPeriod = DateTime.fromObject({
        day: 1,
        month: __quarterStart2Month[timelineQuarter],
        year: timelineYear
      });
      endOfPeriod = startPeriod.endOf('quarter');
      break;
    case 'YEAR':
      startPeriod = DateTime.fromObject({
        day: 1,
        month: 1,
        year: timelineYear
      });
      endOfPeriod = startPeriod.endOf('year');

      break;
    default:
      break;
  }

  return {
    startPeriod,
    endOfPeriod
  };
}

